/**
 * CustomerRoleDto ↔ 角色表單三態／遮罩（ticket 11）。
 *
 * 純轉換、不依賴 React／RbacContext —— mock 投影也會引這份,
 * 若再拉 RbacContext → sessionStore 會形成循環依賴搞掛 mock 註冊。
 *
 * 後端: modules[].access（Edit/View/Hidden）、fields[].enabled
 * 表單: edit/read/hidden、PERM_FIELD_MASKS keys
 */

/**
 * 表單模組 key → 後端 module_key（**寫入**用）。
 *
 * ⚠️ 這三個名字是 2026-08-19 smoke round-trip 實測改的：
 * `Registry`／`Monitor`／`Notify` 送進 `Create`／`Update`，**後端收不到**——
 * 存檔會回 200，讀回來那三列卻是 `Hidden`。也就是使用者把「營運資訊」
 * 「監控派工」「訊息功能」設成可編輯、按存檔、看到成功，重新載入變回無權限，
 * 而且沒有任何錯誤訊息（前端不知道那三個鍵被丟掉了）。
 *
 * 後端在用的是 `Operation`／`Monitoring`／`Message`（讀取側的 alias 表早就有
 * 這三個，8/12 實測加的 —— 但**只加在讀，沒有加在寫**，所以錯了一路）。
 * 實測：送這三個名字，三個都存得進去。
 */
export const PERM_KEY_TO_MODULE_KEY = {
  user: "People",
  dashboard: "Dashboard",
  room: "Room",
  registry: "Operation",
  asset: "Asset",
  monitor: "Monitoring",
  notify: "Message",
  report: "Report",
  decision: "Decision",
  approval: "Approval",
  import: "Import",
};

/** 後端 module_key（含 8/12 實測別名）→ 表單資源名,供 levelsFromModules 涵蓋。
 * 與 RbacContext.MODULE_KEY_TO_RESOURCES 同內容 —— 兩邊改要一起改
 * (ADR-0004:別名表互相指路;本檔不能 import RbacContext,否則 mock 循環依賴)。
 */
export const MODULE_KEY_TO_FORM_RESOURCES = {
  People: ["user", "work", "role"],
  Dashboard: ["dashboard"],
  Room: ["room"],
  Registry: ["ip", "project", "contract", "vendor"],
  Asset: ["cabinet", "hardware", "software"],
  Monitor: ["monitor"],
  Notify: ["notify"],
  Report: ["report"],
  Decision: ["decision"],
  Approval: ["approval"],
  Import: ["import"],
  Operation: ["ip", "project", "contract", "vendor"],
  Monitoring: ["monitor"],
  Message: ["notify"],
};

/** 表單列 → 要看哪些資源才能決定該列三態 */
const FORM_ROW_RESOURCES = {
  user: ["user", "work", "role"],
  dashboard: ["dashboard"],
  room: ["room"],
  registry: ["ip", "project", "contract", "vendor"],
  asset: ["cabinet", "hardware", "software"],
  monitor: ["monitor"],
  notify: ["notify"],
  report: ["report"],
  decision: ["decision"],
  approval: ["approval"],
  import: ["import"],
};

const LEVEL_TO_ACCESS = { edit: "Edit", read: "View", hidden: "Hidden" };
// 反向(access → level)沒有對照表:一個表單列可能對到多個資源,要取「最寬的那個」,
// 不是逐一查表 —— 換算在 levelsFromModules 裡用 rank 做。

export const MASK_KEY_TO_FIELD_KEY = {
  contractAmount: "ContractAmount",
  assetAmount: "AssetAmount",
  reportAmount: "ReportAmount",
  decisionAmount: "DecisionAmount",
  // WF_v3 新增(票 21):訊息暫停送審限 Overseer。swagger v1.00 尚無此
  // field_key,PascalCase 比照契約慣例先行 —— 已列入回報後端清單。
  notifyPause: "NotifyPause",
};

export const FIELD_KEY_TO_MASK_KEY = Object.fromEntries(
  Object.entries(MASK_KEY_TO_FIELD_KEY).map(([k, v]) => [v, k])
);

const FORM_KEYS = Object.keys(PERM_KEY_TO_MODULE_KEY);
const MASK_KEYS = Object.keys(MASK_KEY_TO_FIELD_KEY);

/** levels → modules[] */
export function modulesFromLevels(levels = {}) {
  return FORM_KEYS.map((key) => ({
    module_key: PERM_KEY_TO_MODULE_KEY[key],
    access: LEVEL_TO_ACCESS[levels[key]] || "Hidden",
  }));
}

/** masks → fields[]（true=顯示＝enabled） */
export function fieldsFromMasks(masks = {}) {
  return MASK_KEYS.map((key) => ({
    field_key: MASK_KEY_TO_FIELD_KEY[key],
    enabled: !!masks[key],
  }));
}

/**
 * modules[] → levels。讀取時涵蓋別名（Operation/Monitoring/Message）,
 * 一列取最寬存取。
 */
export function levelsFromModules(modules = []) {
  const accessRank = { Edit: 3, View: 2, Hidden: 1 };
  const bestByResource = {};
  (modules || []).forEach((m) => {
    const rank = accessRank[m.access] || 0;
    (MODULE_KEY_TO_FORM_RESOURCES[m.module_key] || []).forEach((res) => {
      if (!bestByResource[res] || rank > accessRank[bestByResource[res]]) {
        bestByResource[res] = m.access;
      }
    });
  });
  return FORM_KEYS.reduce((acc, key) => {
    let best = 0;
    (FORM_ROW_RESOURCES[key] || []).forEach((res) => {
      const r = accessRank[bestByResource[res]] || 0;
      if (r > best) best = r;
    });
    acc[key] = best >= 3 ? "edit" : best >= 2 ? "read" : "hidden";
    return acc;
  }, {});
}

export function masksFromFields(fields = []) {
  const byKey = {};
  (fields || []).forEach((f) => {
    const maskKey = FIELD_KEY_TO_MASK_KEY[f.field_key];
    if (maskKey) byKey[maskKey] = !!f.enabled;
  });
  return MASK_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: byKey[key] !== undefined ? byKey[key] : false }),
    {}
  );
}

/** 角色代碼：契約 system_name；舊 mock 列用 code */
export const roleCodeOf = (role) =>
  (role && (role.system_name || role.code)) || "";

/**
 * 這個角色目前的配置。
 * fallbackMatrix(code) 由呼叫端注入(通常是 capabilityMatrix / fieldMaskMatrix),
 * 避免本檔依賴 RbacContext。
 *
 * 四正式名:走 fallback(客戶確認單／CAPABILITIES 真源)。
 * 真後端其他 is_system_role(如 Administrators):fallback 空 → 改吃 DTO modules/fields。
 */
export const levelsOf = (role, fallbackMatrix) => {
  if (!role) return {};
  if (role.levels) return role.levels;
  const code = roleCodeOf(role);
  const fourBuiltIn = ["Overseer", "Wrangler", "Viewer", "Gatekeeper"].includes(code);
  if (fourBuiltIn && typeof fallbackMatrix === "function") {
    return fallbackMatrix(code) || {};
  }
  if (role.modules && role.modules.length) return levelsFromModules(role.modules);
  if (typeof fallbackMatrix === "function") {
    const fb = fallbackMatrix(code) || {};
    if (Object.keys(fb).length) return fb;
  }
  return {};
};

export const masksOf = (role, fallbackMatrix) => {
  if (!role) return {};
  if (role.masks) return role.masks;
  const code = roleCodeOf(role);
  const fourBuiltIn = ["Overseer", "Wrangler", "Viewer", "Gatekeeper"].includes(code);
  if (fourBuiltIn && typeof fallbackMatrix === "function") {
    return fallbackMatrix(code) || {};
  }
  if (role.fields && role.fields.length) return masksFromFields(role.fields);
  if (typeof fallbackMatrix === "function") {
    const fb = fallbackMatrix(code) || {};
    if (Object.keys(fb).length) return fb;
  }
  return {};
};

/** 表單草稿 → Create/Update body（CustomerRoleDto 形狀） */
export function draftToCustomerRoleBody(draft, { id } = {}) {
  const body = {
    name: (draft.name || "").trim(),
    description: (draft.description || "").trim(),
    system_name: draft.system_name || draft.code || (draft.name || "").trim(),
    active: draft.active !== false,
    is_system_role: false,
    modules: modulesFromLevels(draft.levels || {}),
    fields: fieldsFromMasks(draft.masks || {}),
  };
  if (id != null) body.id = id;
  return body;
}
